import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Typography,
    Box,
    Divider,
    InputAdornment,
} from '@mui/material';
import {
    Close as CloseIcon,
    FilterAlt as FilterIcon,
    RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SttSelectSport } from '../../../sporttia/selectors/SttSelectSport';
import { SelectGroupField } from '../../../sporttia/all';
import { SttSelectCity } from '../../../sporttia/selectors/SttSelectCity';

type FilterValues = {
    name?: string;
    login?: string;
    email?: string;
    gender?: string | null;
    minPurseAmount?: number | null;
    minDebt?: number | null;
    certified?: string | null;
    sport?: { id: number; name: string } | null;
    group?: { id: number; name: string } | null;
    dni?: string;
    city?: { id: number; name: string } | null;
    postalCode?: string | null;
    mobile?: string;
    minAge?: number | null;
    maxAge?: number | null;
    trash?: boolean;
};

type Props = {
    open: boolean;
    onClose: () => void;
    onApply: (filters: FilterValues) => void;
    initialFilters?: Partial<FilterValues> | Record<string, unknown>;
};

const defaultFilters: FilterValues = {
    name: '',
    login: '',
    email: '',
    gender: null,
    minPurseAmount: null,
    minDebt: null,
    certified: null,
    sport: null,
    group: null,
    dni: '',
    city: null,
    postalCode: '',
    mobile: '',
    minAge: null,
    maxAge: null,
    trash: false,
};

export default function MshipsFilterDialog({
    open,
    onClose,
    onApply,
    initialFilters = {},
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const [filters, setFilters] = useState<FilterValues>({
        ...defaultFilters,
        ...initialFilters,
    });

    useEffect(() => {
        if (open) {
            setFilters({ ...defaultFilters, ...initialFilters });
        }
    }, [open, initialFilters]);

    const handleChange = (name: string, value: unknown) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleReset = () => {
        setFilters(defaultFilters);
    };

    const handleApply = () => {
        onApply(filters);
        onClose();
    };

    const getActiveFiltersCount = (): number => {
        let count = 0;
        if (filters.name) count++;
        if (filters.login) count++;
        if (filters.email) count++;
        if (filters.gender) count++;
        if (filters.minPurseAmount) count++;
        if (filters.minDebt) count++;
        if (filters.certified) count++;
        if (filters.sport?.id) count++;
        if (filters.group?.id) count++;
        if (filters.dni) count++;
        if (filters.city?.id) count++;
        if (filters.postalCode) count++;
        if (filters.mobile) count++;
        if (filters.minAge) count++;
        if (filters.maxAge) count++;
        return count;
    };

    const activeCount = getActiveFiltersCount();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterIcon color="primary" />
                    <Typography variant="h6" component="span" fontWeight={600}>
                        {t('generic.filters')}
                    </Typography>
                    {activeCount > 0 && (
                        <Box
                            sx={{
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                                borderRadius: '12px',
                                px: 1,
                                py: 0.25,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                            }}
                        >
                            {activeCount}
                        </Box>
                    )}
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 3 }}>
                <Grid container spacing={2}>
                    {/* Row 1: Name, Login, Email */}
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('generic.name')}
                            value={filters.name || ''}
                            onChange={(e) =>
                                handleChange('name', e.target.value)
                            }
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('user.login')}
                            value={filters.login || ''}
                            onChange={(e) =>
                                handleChange('login', e.target.value)
                            }
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('generic.email')}
                            value={filters.email || ''}
                            onChange={(e) =>
                                handleChange('email', e.target.value)
                            }
                        />
                    </Grid>

                    {/* Row 2: DNI, Mobile, Gender */}
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('generic.dni')}
                            value={filters.dni || ''}
                            onChange={(e) =>
                                handleChange('dni', e.target.value)
                            }
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('generic.mobile')}
                            value={filters.mobile || ''}
                            onChange={(e) =>
                                handleChange('mobile', e.target.value)
                            }
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>{t('user.gender')}</InputLabel>
                            <Select
                                value={filters.gender || ''}
                                label={t('user.gender')}
                                onChange={(e) =>
                                    handleChange(
                                        'gender',
                                        e.target.value || null,
                                    )
                                }
                            >
                                <MenuItem value="">
                                    <em>{t('generic.all')}</em>
                                </MenuItem>
                                <MenuItem value="MALE">
                                    {t('user.male')}
                                </MenuItem>
                                <MenuItem value="FEMALE">
                                    {t('user.female')}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Row 3: Age range, Certified */}
                    <Grid size={{ xs: 6, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('activityLabels.minAge')}
                            type="number"
                            value={filters.minAge ?? ''}
                            onChange={(e) =>
                                handleChange(
                                    'minAge',
                                    e.target.value
                                        ? Number(e.target.value)
                                        : null,
                                )
                            }
                        />
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('activityLabels.maxAge')}
                            type="number"
                            value={filters.maxAge ?? ''}
                            onChange={(e) =>
                                handleChange(
                                    'maxAge',
                                    e.target.value
                                        ? Number(e.target.value)
                                        : null,
                                )
                            }
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>{t('generic.certified')}</InputLabel>
                            <Select
                                value={filters.certified || ''}
                                label={t('generic.certified')}
                                onChange={(e) =>
                                    handleChange(
                                        'certified',
                                        e.target.value || null,
                                    )
                                }
                            >
                                <MenuItem value="">
                                    <em>{t('generic.all')}</em>
                                </MenuItem>
                                <MenuItem value="S">
                                    {t('generic.yes')}
                                </MenuItem>
                                <MenuItem value="N">{t('generic.no')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Row 4: City, Postal Code */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SttSelectCity
                            grid={false}
                            caption={t('generic.city')}
                            name="city"
                            defVal={filters.city?.name || ''}
                            onChange={() => {}}
                            onSelect={({ value }) =>
                                handleChange('city', value)
                            }
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('generic.postalCode')}
                            value={filters.postalCode || ''}
                            onChange={(e) =>
                                handleChange('postalCode', e.target.value)
                            }
                        />
                    </Grid>

                    {/* Row 5: Purse, Debt */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('generic.purse')}
                            type="number"
                            value={filters.minPurseAmount ?? ''}
                            onChange={(e) =>
                                handleChange(
                                    'minPurseAmount',
                                    e.target.value
                                        ? Number(e.target.value)
                                        : null,
                                )
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        €
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('labels.debt')}
                            type="number"
                            value={filters.minDebt ?? ''}
                            onChange={(e) =>
                                handleChange(
                                    'minDebt',
                                    e.target.value
                                        ? Number(e.target.value)
                                        : null,
                                )
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        €
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    {/* Row 6: Sport, Group */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SttSelectSport
                            mode="dialog"
                            fullWidth
                            defVal={filters.sport?.name || ''}
                            onChange={(sport) =>
                                handleChange('sport', sport?.id ? sport : null)
                            }
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SelectGroupField
                            caption={t('generic.group')}
                            name="group"
                            defVal={filters.group}
                            onChange={({ value }) =>
                                handleChange('group', value)
                            }
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <Divider />

            <DialogActions
                sx={{ px: 3, py: 2, justifyContent: 'space-between' }}
            >
                <Button
                    startIcon={<ResetIcon />}
                    onClick={handleReset}
                    color="inherit"
                >
                    {t('buttons.reset')}
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={onClose} color="inherit">
                        {t('generic.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleApply}
                        startIcon={<FilterIcon />}
                    >
                        {t('generic.apply')}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
